import React, { createContext, useContext, useEffect, useState } from "react";
import { Loader, ReactChildrenProps } from "ecom-teams-apps-core";
import { observer } from "mobx-react-lite";
import pl from "shared/messages/pl";
import { useStore } from "stores/Stores";
import styles from "./authProvider.module.scss";
import LoginModal from "modals/LoginModal";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { MsTeamsContext } from "msTeams/store";
import { UserOpenedApplicationEvent } from "appilication-insights/events/UserOpenedApplication/UserOpenedApplicationEvent";
import { trackEvent } from "appilication-insights/ApplicationInsightsUtils";
import ErrorContent from "shared/components/errorContent/ErrorContent";
import { GrantEnum } from "view360Card/models/GrantEnum";
import * as microsoftTeams from "@microsoft/teams-js";

export interface AuthContextProps {
    isUserGrantedTo: (grant: GrantEnum | undefined) => boolean;
}

export const defaultAuthContextProps: AuthContextProps = {
    isUserGrantedTo: (grant: GrantEnum | undefined) => true,
};

export const AuthContext = createContext(defaultAuthContextProps);

const AuthProvider: React.FC<ReactChildrenProps> = observer((props: ReactChildrenProps) => {
    const { authorizationStore, configStore } = useStore();

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");

    const appInsightsContext = useAppInsightsContext();
    const msTeamsContext = useContext(MsTeamsContext);

    useEffect(() => {
        const initialize = async () => {
            if (configStore?.config?.production) {
                await authorizationStore.authorize(false);
            } else {
                setShowLoginModal(true);
            }

            const usesDevAuthTokenMechanism = process.env.REACT_APP_USE_LOCAL_TEAMS === "true";
            const isNonProdEnvironment =
                configStore?.config?.production === false ||
                (configStore?.config?.production === true && process.env.REACT_APP_USE_LOCAL_TEAMS === "true");
            const isInsideMsTeams = process.env.REACT_APP_USE_WITH_NO_TEAMS !== "true";

            if (isInsideMsTeams && (isNonProdEnvironment || usesDevAuthTokenMechanism)) {
                // Log authentication data to console
                const [accessToken, context] = await Promise.all([
                    microsoftTeams.authentication.getAuthToken(),
                    microsoftTeams.app.getContext(),
                ]);
                console.log(context);
                console.log(accessToken);
            }
        };
        initialize();
    }, []);

    useEffect(() => {
        if (msTeamsContext.isInitialized && msTeamsContext.userObjectId && authorizationStore.currentUser) {
            const appOpenedEvent = new UserOpenedApplicationEvent(
                authorizationStore.currentUser,
                msTeamsContext.userObjectId,
                msTeamsContext.userPrincipalName
            );
            trackEvent(appInsightsContext, appOpenedEvent, appOpenedEvent.getCustomProperties());
        }
    }, [msTeamsContext.isInitialized, authorizationStore.currentUser]);

    const developmentAuth = async () => {
        if (!login?.length || !password?.length) {
            alert(pl.authProvider.developmentAuth.validationError);
            return;
        }

        await authorizationStore.authorize(true, false, login, password);
        setShowLoginModal(false);
    };

    const renderLoader = () => {
        return (
            <div className={styles.loaderWrapper}>
                <Loader text={pl.common.appLoading} />
            </div>
        );
    };

    const renderErrorModal = () => {
        return <ErrorContent title={pl.authProvider.authError.header} />;
    };

    const renderLoginModal = () => {
        return (
            <LoginModal
                setLogin={(login) => setLogin(login)}
                setPassword={(password) => setPassword(password)}
                onAuthorize={developmentAuth}
            />
        );
    };

    const isUserGrantedTo = (grant: GrantEnum | undefined) => {
        if ((configStore.config?.featureFlags.grants ?? false) === false) {
            return true;
        }

        if (grant === undefined) {
            return true;
        }

        if (
            authorizationStore.currentUser &&
            authorizationStore.currentUser?.grants.findIndex((g) => g === grant) >= 0
        ) {
            return true;
        }

        return false;
    };

    return showLoginModal ? (
        renderLoginModal()
    ) : !authorizationStore.authorizeRequestStatus.isDone ? (
        renderLoader()
    ) : authorizationStore.authorizeRequestStatus.error ? (
        renderErrorModal()
    ) : (
        <AuthContext.Provider
            value={{
                isUserGrantedTo: isUserGrantedTo,
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
});

export default AuthProvider;
